/**
 * `ClockSyncEstimator` — a rolling NTP-style clock-offset/RTT/jitter estimator (#247,
 * NFR-6.7). Pure and deterministic: all clocks are injected, so unit tests drive it with
 * synthetic samples and no wall time.
 *
 * For each four-timestamp sample (`c1`,`s1`,`s2`,`c2`, see {@link SyncSample}) it derives
 * a per-sample RTT and offset (NTP math, see ALGORITHM.md), keeps a bounded window of the
 * most recent samples, and reports an aggregate {@link SyncState}. The offset is taken
 * from the **lowest-RTT** sample in the window — the sample least distorted by transient
 * queueing — rather than an average that a single laggy round-trip would skew.
 *
 * The {@link markSuspect} seam lets a caller invalidate the estimate on a state
 * transition (page visibility change, WS reconnect): it drops the window so stale
 * pre-transition samples can't corrupt the offset, and holds `suspect` until enough fresh
 * samples arrive.
 */

import type { SyncQuality, SyncSample, SyncState } from './types';

/** Per-grade RTT + jitter ceilings (ms). A sample set clears a grade if it meets both. */
export interface QualityThresholds {
  /** Grade A: tightest. */
  gradeA: { rttP95Ms: number; jitterMs: number };
  /** Grade B: usable. Anything worse (or too few samples / suspect) is C. */
  gradeB: { rttP95Ms: number; jitterMs: number };
}

export interface ClockSyncEstimatorOptions {
  /** Max samples retained (rolling window). Default 16. */
  windowSize?: number;
  /** Min samples before a non-C grade is possible. Default 3. */
  minSamples?: number;
  /** Fresh samples required after {@link markSuspect} before `suspect` clears. Default 3. */
  minSamplesAfterSuspect?: number;
  /** Monotonic clock for {@link SyncState.sampleAgeMs}. Defaults to a monotonic source. */
  now?: () => number;
  /** Grade ceilings; sensible LAN/Wi-Fi defaults otherwise. */
  qualityThresholds?: QualityThresholds;
}

const DEFAULT_THRESHOLDS: QualityThresholds = {
  gradeA: { rttP95Ms: 80, jitterMs: 30 },
  gradeB: { rttP95Ms: 250, jitterMs: 100 },
};

function defaultNow(): number {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }
  return Date.now();
}

interface WindowEntry {
  rtt: number;
  offset: number;
  /** The sample's client-receive time (`c2`) — the reference for sample age. */
  c2: number;
}

/** RTT of a sample: total round-trip minus the server's processing time. */
export function sampleRtt(s: SyncSample): number {
  return s.c2 - s.c1 - (s.s2 - s.s1);
}

/** Offset (server − client) of a sample: mean of the forward and return path skews. */
export function sampleOffset(s: SyncSample): number {
  return (s.s1 - s.c1 + (s.s2 - s.c2)) / 2;
}

/** Nearest-rank percentile over an already-sorted-ascending numeric array. */
function percentile(sortedAsc: number[], p: number): number {
  if (sortedAsc.length === 0) return 0;
  if (sortedAsc.length === 1) return sortedAsc[0]!;
  const rank = Math.ceil((p / 100) * sortedAsc.length);
  const idx = Math.min(sortedAsc.length - 1, Math.max(0, rank - 1));
  return sortedAsc[idx]!;
}

/** Population standard deviation. */
function stddev(values: number[]): number {
  if (values.length < 2) return 0;
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const variance =
    values.reduce((acc, v) => acc + (v - mean) * (v - mean), 0) / values.length;
  return Math.sqrt(variance);
}

export class ClockSyncEstimator {
  private readonly window: WindowEntry[] = [];
  private readonly windowSize: number;
  private readonly minSamples: number;
  private readonly minSamplesAfterSuspect: number;
  private readonly now: () => number;
  private readonly thresholds: QualityThresholds;
  private suspectFlag = false;
  /** Fresh samples added since the last {@link markSuspect}. */
  private samplesSinceSuspect = 0;

  constructor(options: ClockSyncEstimatorOptions = {}) {
    this.windowSize = Math.max(1, options.windowSize ?? 16);
    this.minSamples = Math.max(1, options.minSamples ?? 3);
    this.minSamplesAfterSuspect = Math.max(1, options.minSamplesAfterSuspect ?? 3);
    this.now = options.now ?? defaultNow;
    this.thresholds = options.qualityThresholds ?? DEFAULT_THRESHOLDS;
  }

  /** Number of samples currently retained. */
  get sampleCount(): number {
    return this.window.length;
  }

  /**
   * Record a completed round-trip sample. A sample with a non-finite or negative RTT
   * (impossible timing — clock glitch / reordered frames) is rejected so it can't poison
   * the window.
   */
  addSample(sample: SyncSample): boolean {
    const rtt = sampleRtt(sample);
    if (!Number.isFinite(rtt) || rtt < 0) return false;
    const offset = sampleOffset(sample);
    if (!Number.isFinite(offset)) return false;
    this.window.push({ rtt, offset, c2: sample.c2 });
    while (this.window.length > this.windowSize) this.window.shift();
    if (this.suspectFlag) {
      this.samplesSinceSuspect += 1;
      if (this.samplesSinceSuspect >= this.minSamplesAfterSuspect) {
        this.suspectFlag = false;
      }
    }
    return true;
  }

  /**
   * Invalidate the estimate on a state transition (visibility change / reconnect). Drops
   * the window so pre-transition samples can't corrupt the offset, and holds `suspect`
   * until {@link minSamplesAfterSuspect} fresh samples arrive.
   */
  markSuspect(): void {
    this.window.length = 0;
    this.suspectFlag = true;
    this.samplesSinceSuspect = 0;
  }

  /** Reset to the never-synced state. */
  reset(): void {
    this.window.length = 0;
    this.suspectFlag = false;
    this.samplesSinceSuspect = 0;
  }

  /**
   * The current aggregate estimate. `nowMonoMs` overrides the injected clock for a
   * deterministic {@link SyncState.sampleAgeMs} in tests.
   */
  getState(nowMonoMs?: number): SyncState {
    const n = this.window.length;
    if (n === 0) {
      return {
        offsetMs: 0,
        rttP50: 0,
        rttP95: 0,
        jitterMs: 0,
        quality: 'C',
        uncertaintyMs: 0,
        sampleAgeMs: 0,
        suspect: this.suspectFlag,
        sampleCount: 0,
      };
    }
    const rtts = this.window.map((e) => e.rtt);
    const sortedRtts = [...rtts].sort((a, b) => a - b);
    const rttP50 = percentile(sortedRtts, 50);
    const rttP95 = percentile(sortedRtts, 95);
    const jitterMs = stddev(rtts);
    const minRtt = sortedRtts[0]!;

    // Offset from the lowest-RTT sample — least distorted by transient queueing.
    let best = this.window[0]!;
    for (const e of this.window) if (e.rtt < best.rtt) best = e;
    const offsetMs = best.offset;

    // A single ping bounds the offset error by ±RTT/2; jitter widens it.
    const uncertaintyMs = minRtt / 2 + jitterMs;

    const newest = this.window[n - 1]!;
    const nowMs = nowMonoMs ?? this.now();
    const sampleAgeMs = Math.max(0, nowMs - newest.c2);

    return {
      offsetMs,
      rttP50,
      rttP95,
      jitterMs,
      quality: this.grade(rttP95, jitterMs),
      uncertaintyMs,
      sampleAgeMs,
      suspect: this.suspectFlag,
      sampleCount: n,
    };
  }

  private grade(rttP95: number, jitterMs: number): SyncQuality {
    if (this.suspectFlag || this.window.length < this.minSamples) return 'C';
    const { gradeA, gradeB } = this.thresholds;
    if (rttP95 <= gradeA.rttP95Ms && jitterMs <= gradeA.jitterMs) return 'A';
    if (rttP95 <= gradeB.rttP95Ms && jitterMs <= gradeB.jitterMs) return 'B';
    return 'C';
  }
}
